import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Rating
} from '@mui/material';
// Note: Star icons are not available in Feather Icons, keeping MUI for now
// You may want to use a different icon or install react-icons for star icons
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { courseAPI, chapterRatingAPI } from '../services/api';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import '../styles/components/_chapter-rating.scss';
import { useDiagnosticAccessGuard } from '../hooks/useDiagnosticAccessGuard';

const ChapterRating: React.FC = () => {
  const { courseId, chapterId } = useParams<{ 
    courseId: string; 
    chapterId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [course, setCourse] = useState<any>(null);
  const [chapter, setChapter] = useState<any>(null);
  const [rating, setRating] = useState<number>(0);
  const [existingRating, setExistingRating] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useDiagnosticAccessGuard();

  useEffect(() => {
    if (courseId && chapterId) {
      loadData();
    }
  }, [courseId, chapterId]);

  const loadData = async () => {
    if (!courseId || !chapterId) {
      setError(t('common.missingParameters'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Load course data
      const courseData = await courseAPI.getCourse(courseId);
      setCourse(courseData);

      // Find the chapter
      const chapterData = courseData.chapters?.find((ch: any) => ch._id === chapterId);
      if (chapterData) {
        setChapter(chapterData);
        if (chapterData.requiresRating === false) {
          const nextChapterId = searchParams.get('next');
          navigate(`/course/${courseId}/chapter/${chapterId}/summary${nextChapterId ? `?next=${nextChapterId}` : ''}`, { replace: true });
          return;
        }
      } else {
        setError(t('chapterRating.chapterNotFound'));
        setLoading(false);
        return;
      }

      // Load existing rating if any
      try {
        const ratingData = await chapterRatingAPI.getRating(courseId, chapterId);
        if (ratingData.rating) {
          setExistingRating(ratingData.rating);
          setRating(ratingData.rating.rating);
          // If rating already exists, skip to summary page
          const nextChapterId = searchParams.get('next');
          navigate(`/course/${courseId}/chapter/${chapterId}/summary${nextChapterId ? `?next=${nextChapterId}` : ''}`, { replace: true });
          return;
        }
      } catch (err: any) {
        // No existing rating, that's fine - user needs to rate
        console.log('No existing rating found');
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(getLearnerFacingApiError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRating = (newValue: number) => {
    if (saving) return;
    setRating(newValue);
  };

  const handleSubmit = async () => {
    if (!courseId || !chapterId || !rating) return;

    try {
      setSaving(true);
      setError(null);

      await chapterRatingAPI.saveRating(courseId, chapterId, rating);

      const nextChapterId = searchParams.get('next');
      navigate(`/course/${courseId}/chapter/${chapterId}/summary${nextChapterId ? `?next=${nextChapterId}` : ''}`);
    } catch (err: any) {
      console.error('Error saving rating:', err);
      setError(getLearnerFacingApiError(err, t));
      setSaving(false);
    }
  };


  // Show loading state
  if (loading) {
    return (
      <Box className="chapter-rating__loading">
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error && !chapter) {
    return (
      <Box className="chapter-rating__error">
        <Typography variant="h6" className="chapter-rating__error-text">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="chapter-rating">
      {/* Main Content Card */}
      <Box className="chapter-rating__card">
        {/* Emoji and Title Section */}
        <Box className="chapter-rating__header-section">
          <Box className="chapter-rating__emoji-container">
            <span className="chapter-rating__emoji">👏</span> 
          </Box>
          <p className="chapter-rating__title">
            {t('chapterRating.title')}
          </p>
        </Box>

        {/* Question Text */}
        <Box className="chapter-rating__question-section">
          <p className="chapter-rating__question">
            {t('chapterRating.question1')}
          </p>
          <p className="chapter-rating__question">
            {t('chapterRating.question2')}
          </p>
        </Box>

        {error && (
          <Box className="chapter-rating__error-message">
            <Typography variant="body2" className="chapter-rating__error-text">
              {error}
            </Typography>
          </Box>
        )}

        {/* Rating Section with Numbered Stars */}
        <Box className="chapter-rating__rating-section">
          <Box className="chapter-rating__stars-container">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((starValue) => {
              const isSelected = rating >= starValue;
              let starColor = '#ff4d4d'; // Red for 1-6
              if (starValue >= 9) {
                starColor = '#4caf50'; // Green for 9-10
              } else if (starValue >= 7) {
                starColor = '#ffc107'; // Yellow for 7-8
              }

              return (
                <Box
                  key={starValue}
                  className={`chapter-rating__star-wrapper ${isSelected ? 'chapter-rating__star-wrapper--selected' : ''}`}
                  onClick={() => handleSelectRating(starValue)}
                  style={{ cursor: saving ? 'default' : 'pointer' }}
                >
                  <Box
                    className="chapter-rating__star"
                    sx={{
                      borderColor: starColor,
                      backgroundColor: isSelected ? starColor : 'transparent'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      className="chapter-rating__star-number"
                      sx={{ 
                        color: isSelected ? '#fff' : starColor,
                        fontWeight: 700
                      }}
                    >
                      {starValue}
                    </Typography>
                  </Box>
                  {starValue === 1 && (
                    <Typography variant="caption" className="chapter-rating__star-label chapter-rating__star-label--left">
                      {t('chapterRating.labels.nul')}
                    </Typography>
                  )}
                  {starValue === 10 && (
                    <Typography variant="caption" className="chapter-rating__star-label chapter-rating__star-label--right">
                      {t('chapterRating.labels.top')}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
          {saving && (
            <Typography variant="body2" className="chapter-rating__saving-text">
              {t('chapterRating.saving')}
            </Typography>
          )}

          <Box sx={{ mt: 3, width: '100%' }}>
            <Button
              variant="contained"
              disabled={saving || !rating}
              onClick={handleSubmit}
              className="button-primary"
              fullWidth
            >
              {t('common.continue') ?? 'Valider et continuer'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChapterRating;
